import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';

import { useEventfy } from '@hooks';
import {
  selectLeftNewsFeedBanners,
  selectRightNewsFeedBanners,
} from '@hooks/useEventfy/selectors';

import * as S from './styles';

type BannerCarouselProps = {
  side: 'left' | 'right';
};

type BannerData = {
  url_image: string;
  action: string | null;
  target: string | null;
  limit_time: number;
};

const renderBannerContent = (banner: BannerData): ReactNode => {
  const image = <img src={banner.url_image} alt={banner.action || ''} />;

  if (!banner.action) return image;

  const target = typeof banner.target === 'object' ? undefined : banner.target;

  return (
    <a href={banner.action} target={target} rel="noopener noreferrer">
      {image}
    </a>
  );
};

const BannerCarousel = ({ side }: BannerCarouselProps) => {
  const bannersSelector =
    side === 'left' ? selectLeftNewsFeedBanners : selectRightNewsFeedBanners;

  const banners = useEventfy(bannersSelector);

  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);

  const hasMultipleBanners = banners.length > 1;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  const startAutoplay = useCallback(() => {
    if (!hasMultipleBanners) return;

    clearTimer();

    const duration = (banners[activeIndex]?.limit_time || 5) * 1000;

    intervalRef.current = setInterval(() => {
      if (!isPausedRef.current) {
        setActiveIndex((prev) => (prev + 1) % banners.length);
      }
    }, duration);
  }, [hasMultipleBanners, banners, activeIndex, clearTimer]);

  useEffect(() => {
    startAutoplay();
    return clearTimer;
  }, [startAutoplay, clearTimer]);

  if (!banners.length) {
    return null;
  }

  return (
    <S.Container>
      <S.CarouselTrack
        onMouseEnter={() => {
          isPausedRef.current = true;
        }}
        onMouseLeave={() => {
          isPausedRef.current = false;
        }}
      >
        {banners.map((banner, index) => (
          <S.Slide key={banner.url_image} $active={index === activeIndex}>
            {renderBannerContent(banner)}
          </S.Slide>
        ))}

        {hasMultipleBanners && (
          <S.DotsContainer>
            {banners.map((banner, index) => (
              <S.Dot
                key={`dot-${banner.url_image}`}
                type="button"
                $active={index === activeIndex}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </S.DotsContainer>
        )}
      </S.CarouselTrack>
    </S.Container>
  );
};

export default BannerCarousel;
